import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, ArrowUpRight, Pause, Play } from 'lucide-react';
import { Icon } from './Shared';
import './Showcase.css';

const useMobilePoster = () => {
  const [mobile, setMobile] = useState(() => window.matchMedia?.('(max-width: 650px)').matches || false);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 650px)');
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  return mobile;
};

const CinematicSlide = ({ slide, index, active, displayed, paused, reduced, failed, mobile, onReady, onError }) => {
  const video = useRef();
  const visible = index === displayed;
  const shared = {
    className: `showcase-image ${visible ? 'is-visible' : ''}`,
    style: { '--slide-position': slide.position, '--slide-mobile-position': slide.mobilePosition, animationPlayState: !paused && !reduced && visible ? 'running' : 'paused' },
    initial: false,
    animate: { opacity: visible ? 1 : 0 },
    transition: { duration: reduced ? 0 : 1.25 },
    'aria-hidden': !visible,
    'data-testid': `hero-healthcare-image-${index}`
  };
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    if (!paused && !reduced && visible && index === active) element.play().catch(() => {});
    else element.pause();
  }, [active, displayed, index, paused, reduced, visible]);
  if (reduced || !slide.video || failed) return <motion.img
    {...shared} src={slide.image} srcSet={`${slide.mobileImage} 768w, ${slide.image} ${slide.width}w`}
    sizes="(max-width:650px) 100vw, 76vw" alt={slide.alt} width={slide.width} height={slide.height}
    decoding="async" fetchPriority={index === active ? 'high' : 'low'} onLoad={() => onReady(index)} onError={() => onError(index)}/>;
  return <motion.video
    {...shared} className={`${shared.className} showcase-video`} ref={video} poster={mobile ? slide.mobileImage : slide.image}
    width={slide.width} height={slide.height} muted loop playsInline disablePictureInPicture
    preload={index === active ? 'auto' : 'metadata'} onLoadedData={() => onReady(index)}
    role="img" aria-label={slide.alt} tabIndex={-1}>
    <source src={slide.video} type="video/mp4"/>
    <source src={slide.webm} type="video/webm" onError={() => onError(index)}/>
  </motion.video>;
};

export const CinematicMedia = ({ slides, active, paused, reduced }) => {
  // Load the first view and its neighbour, then fetch other media as needed.
  // Keep the last ready visual visible during a direct jump to an unloaded theme.
  const [requested, setRequested] = useState(() => new Set([0, 1]));
  const [ready, setReady] = useState(() => new Set());
  const [failed, setFailed] = useState(() => new Set());
  const [displayed, setDisplayed] = useState(0);
  const mobile = useMobilePoster();
  useEffect(() => { setRequested(current => new Set([...current, active, (active + 1) % slides.length])); }, [active, slides.length]);
  useEffect(() => { if (ready.has(active)) setDisplayed(active); }, [active, ready]);
  return <div className="cinematic-media" data-testid="cinematic-healthcare-media">
    {slides.map((slide, index) => requested.has(index) && <CinematicSlide
      key={slide.id} slide={slide} index={index} active={active} displayed={displayed}
      paused={paused} reduced={reduced} failed={failed.has(index)} mobile={mobile}
      onReady={item => setReady(current => new Set([...current, item]))}
      onError={item => setFailed(current => new Set([...current, item]))}/>) }
    <div className="cinematic-shade"/><div className="cinematic-grain"/>
    {failed.has(active) && !ready.has(active) && <p className="showcase-image-error" role="status" data-testid="hero-image-error">This visual is temporarily unavailable. You can still explore this theme or choose another slide.</p>}
  </div>;
};

export const CinemaControls = ({ slides, active, setActive, paused, setPaused, reduced, disclosure, onEnquire }) => {
  const slide = slides[active];
  const selectWithKeyboard = (event, index) => {
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % slides.length;
    else if (event.key === 'ArrowLeft') next = (index + slides.length - 1) % slides.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = slides.length - 1;
    else return;
    event.preventDefault();
    setActive(next);
    event.currentTarget.parentElement.querySelectorAll('button')[next]?.focus();
  };
  return <div className="cinema-controls showcase-controls" data-testid="hero-cinema-controls">
    <div className="cinema-controls-main">
      <div className="cinema-caption" aria-live={paused || reduced ? 'polite' : 'off'} aria-atomic="true">
        <span data-testid="hero-current-image-label">{slide.label}</span>
        <p data-testid="hero-current-image-title">{slide.title}</p>
        <div className="showcase-description" data-testid="hero-slide-description">{slide.description}</div>
      </div>
      <div className="showcase-control-actions"><div className="cinema-buttons">
        <button onClick={() => setActive((active + slides.length - 1) % slides.length)} aria-label="Previous showcase image" data-testid="hero-previous-image"><ArrowLeft size={18}/></button>
        <span className="cinema-counter" data-testid="hero-image-counter">{String(active + 1).padStart(2, '0')}<i>/</i>{String(slides.length).padStart(2, '0')}</span>
        <button onClick={() => setActive((active + 1) % slides.length)} aria-label="Next showcase image" data-testid="hero-next-image"><ArrowRight size={18}/></button>
        <button onClick={() => setPaused(!paused)} aria-label={paused || reduced ? 'Play cinematic motion' : 'Pause cinematic motion'} aria-pressed={paused || reduced} disabled={reduced} data-testid="hero-pause-motion">{paused || reduced ? <Play size={16}/> : <Pause size={16}/>}</button>
      </div><a href="#contact" className="showcase-enquire" onClick={() => onEnquire?.(slide.service)} data-testid="hero-slide-enquire">Discuss this focus <ArrowUpRight size={16}/></a></div>
    </div>
    <div className="showcase-topics" role="group" aria-label="Choose an institutional showcase theme" data-testid="hero-showcase-topics">
      {slides.map((item, index) => <button key={item.id} className={index === active ? 'selected' : ''} aria-pressed={index === active} aria-label={`Show slide ${index + 1}: ${item.shortLabel}`} onClick={() => setActive(index)} onKeyDown={event => selectWithKeyboard(event, index)} data-testid={`hero-topic-${index}`}>
        <div className="showcase-topic-top"><span>{String(index + 1).padStart(2, '0')}</span><Icon name={item.icon} size={22}/></div><span>{item.shortLabel}</span>
      </button>)}
    </div>
    <p className="showcase-disclosure" data-testid="hero-imagery-note">{disclosure}</p>
  </div>;
};